import React, { useMemo } from 'react';
import { TextProps } from 'react-native';
import { styled } from 'dripsy';

import { BaseText } from './BaseText';

type ChunkType = {
  highlight: boolean;
  start: number;
  end: number;
};

type HighlightChunkType = {
  start: number;
  length: number;
};

export interface IHighlightTextProps extends TextProps {
  children: string;
  highlightChunks?: Array<HighlightChunkType>;
}

const HighlightedTextComponent = styled(BaseText)({
  bg: '$primary90',
});

export function HighlightText({
  children,
  highlightChunks = [],
  ...props
}: IHighlightTextProps) {
  const chunks = useMemo(() => {
    const allChunks: Array<ChunkType> = [];
    const totalLength = children.length;
    const highlightPositions = highlightChunks;

    function append(start: number, end: number, highlight: boolean) {
      if (end - start > 0) {
        allChunks.push({
          start,
          end,
          highlight,
        });
      }
    }

    if (highlightPositions.length === 0) {
      append(0, totalLength, false);
    } else {
      let lastIndex = 0;

      highlightPositions.forEach((chunk) => {
        append(lastIndex, chunk.start, false);
        append(chunk.start, chunk.start + chunk?.length, true);
        lastIndex = chunk.start + chunk?.length;
      });

      append(lastIndex, totalLength, false);
    }

    return allChunks;
  }, [children, highlightChunks]);

  return (
    <BaseText {...props}>
      {chunks.map((chunk: ChunkType, index: number) => {
        const text = children.substring(chunk.start, chunk.end);

        return chunk.highlight ? (
          <HighlightedTextComponent key={index}>
            {text}
          </HighlightedTextComponent>
        ) : (
          text
        );
      })}
    </BaseText>
  );
}
